import { CreateUserFromEmailPasswordWithMetadataParams, UpdateUserParams, UserWithDetail } from '@/api/user.types';
import axiosInstance from '@/lib/axiosInstance';
import { Paginated, PaginationQueryParams, getPaginatedItemsSchema } from '@/model/common/pagination.model';
import { SortingQueryParams } from '@/model/common/sorting.model';
import { User, UserAvatar, UserAvatarType, UserRole } from '@/model/User.model';
import { UserMetadata } from '@/model/UserMetadata.model';
export interface SearchUsersDto {
  accessCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface SearchUsersByCodesOrEmailsDto {
  accessCodes: string[];
  emails: string[];
}

export interface UpdateUserAvatarDto {
  avatarType: UserAvatarType;
}

export interface SearchUsersByCodesOrEmailsResponse {
  user: User;
  accessCode?: string;
  email?: string;
}

export interface GetUsersFilters {
  role?: UserRole;
  firstname?: string;
  lastname?: string;
  classroomIds?: string[];
  schoolIds?: string[];
}

export const getUserById = async (id: string) => {
  const { data } = await axiosInstance.get(`users/${id}`);
  return User.fromJson(data);
};

/**
 * @deprecated Use generated client hook from src/api/gen/
 * Generated equivalent: userControllerGetMe() in src/api/gen/user.ts
 * Migration completed in Phase 04 profile page migration.
 * Still consumed by AuthContext and useAuthenticatedUser hook — do not remove until those are migrated.
 */
export const getAuthenticatedUser = async () => {
  const { data } = await axiosInstance.get(`users/me`);
  return User.fromJson(data);
};

export const getUserMetadataByUserId = async (userId: string) => {
  const { data } = await axiosInstance.get(`users/${userId}/metadata`);
  return UserMetadata.fromJson(data);
};

export const createUserWithEmailAndPassword = async (createParams: CreateUserFromEmailPasswordWithMetadataParams) => {
  const { data } = await axiosInstance.post('users/create-with-email-and-password', createParams);
  return User.fromJson(data);
};

export const updateUserById = async (userId: string, dto: UpdateUserParams) => {
  const { data } = await axiosInstance.patch(`users/${userId}`, dto);
  return User.fromJson(data);
};

export const searchUsers = async (query: SearchUsersDto): Promise<UserWithDetail[]> => {
  const { data } = await axiosInstance.get(`users/search`, { params: query });
  return data;
};

export const getUserAvatar = async (): Promise<UserAvatar[]> => {
  const { data } = await axiosInstance.get<{ avatars: unknown[] }>('users/avatars');
  return UserAvatar.fromJsonArray(data.avatars);
};

export const updateUserAvatar = async (avatarType: UserAvatarType): Promise<User> => {
  const { data } = await axiosInstance.patch('users/avatar', { avatarType });
  return User.fromJson(data);
};

export const getUsersByFilter = async (
  filters: GetUsersFilters,
  query: PaginationQueryParams & SortingQueryParams
): Promise<Paginated<User>> => {
  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value?.length > 0) {
      queryParams.append(key, value);
    }
  }

  queryParams.append('page', query.page.toString());
  queryParams.append('pageSize', query.pageSize.toString());
  queryParams.append('sortDirection', query.sortDirection);
  queryParams.append('sortBy', query.sortBy);

  const { data } = await axiosInstance.get(`/users`, {
    params: queryParams,
  });

  return getPaginatedItemsSchema(User.zodSchema).parse(data);
};

/**
 * @return Map<[code|email], User>
 */
export const searchUsersByCodesOrEmailsDto = async (dto: SearchUsersByCodesOrEmailsDto): Promise<Map<string, User>> => {
  const { data } = await axiosInstance.post<SearchUsersByCodesOrEmailsResponse[]>(
    `users/search-by-codes-or-emails`,
    dto
  );

  const map = new Map<string, User>();
  data.forEach(({ user, accessCode, email }) => {
    if (accessCode) {
      map.set(accessCode, user);
    }
    if (email) {
      map.set(email, user);
    }
  });

  return map;
};
